import React, { useState, useEffect } from 'react';
import '../../styles/components/WorkplacesUsersList.scss'
import { getAppUserAllById, createAppUser, updateAppUser, deleteAppUserAll } from '../../api/appWorkplacesUsersApi.js';
import { getAppTemplateDomainById } from '../../api/appTemplatesApi.js'
import { getAppKeywordDomainById } from '../../api/appKeyWordsApi.js';
import { getAppDomainUserAttrByUserId, createAppDomainUserAttr, updateAppDomainUserAttr, deleteAppDomainUserAttrAll } from '../../api/domainUserAttributesApi.js'
import { updateAppUserEmailSignature } from '../../api/signatureManagementApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { Box, Grid, Card, CardContent, Typography, Button, Fab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  EditNote as EditNoteIcon,
  EventBusy as EventBusyIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { useDomain } from '../../context/DomainContext.js';
import UserModal from './WorkplacesUserModal.js';
import DeleteModal from '../DeleteModal.js';
import UsersDataGrid from './WorkplacesUserDataGrid.js';
import { useAuth } from '../../context/AuthContext.js';
import MassEditTemplateModal from './MassEditTemplateModal.js';
import features from '../../config/features.js';

function UsersList() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [userToSync, setUserToSync] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const [keywords, setKeywords] = useState([]);
  const [domainUserAttr, setDomainUserAttr] = useState(null);
  const { user } = useAuth();
  
  const [selectedUsersForMassEdit, setSelectedUsersForMassEdit] = useState([]);
  const [openMassEditModal, setOpenMassEditModal] = useState(false);

  // Estados para paginación por tokens en servidor
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [pageTokens, setPageTokens] = useState({ 0: null });
  const [filterModel, setFilterModel] = useState({ items: [] });
  const filterTimeoutRef = React.useRef(null);

  const loadUsers = async (page = paginationModel.page, pageSize = paginationModel.pageSize, tokensMap = pageTokens, currentFilter = filterModel) => {
    setLoadingUsers(true);
    setUsers([]);
    
    try {
      const currentToken = tokensMap[page] || null;
      const emailFilterItem = currentFilter?.items?.find(item => item.field === 'email' && item.value);
      const searchValue = emailFilterItem?.value ? String(emailFilterItem.value).trim() : null;
      const operatorValue = emailFilterItem?.operator || 'startsWith';

      const params = {
        limit: pageSize,
        pageToken: currentToken,
        includeAttributes: features.specialTemplatesEnabled,
        ...(searchValue ? { search: searchValue, operator: operatorValue } : {})
      };

      const response = await getAppUserAllById(selectedDomain, params);
      const data = response.data;

      // Si el backend responde paginado con tokens: { data: [...], nextPageToken: "...", total: N }
      if (data && Array.isArray(data.data)) {
        setUsers(data.data);
        if (typeof data.total === 'number') {
          setRowCount(data.total);
        }
        if (data.nextPageToken) {
          setPageTokens(prev => ({ ...prev, [page + 1]: data.nextPageToken }));
        }
      } else {
        // Modo legado o array plano sin paginación en backend
        const usersData = Array.isArray(data) ? data : [];
        if (features.specialTemplatesEnabled) {
          const usersWithAttributes = await Promise.all(
            usersData.map(async (user) => {
              try {
                const attrResponse = await getAppDomainUserAttrByUserId(user.id);
                const attributes = attrResponse.data?.[0] || attrResponse.data || {};
                return {
                  ...user,
                  birthday: attributes.birthday || '',
                  aniversary: attributes.aniversary || '',
                };
              } catch (error) {
                return { ...user, birthday: '', aniversary: '' };
              }
            })
          );
          setUsers(usersWithAttributes);
          setRowCount(usersWithAttributes.length);
        } else {
          setUsers(usersData);
          setRowCount(usersData.length);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handlePaginationModelChange = (newModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      const resetTokens = { 0: null };
      setPageTokens(resetTokens);
      setPaginationModel({ page: 0, pageSize: newModel.pageSize });
      loadUsers(0, newModel.pageSize, resetTokens, filterModel);
    } else {
      setPaginationModel(newModel);
      loadUsers(newModel.page, newModel.pageSize, pageTokens, filterModel);
    }
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);

    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    filterTimeoutRef.current = setTimeout(() => {
      const initialTokens = { 0: null };
      setPageTokens(initialTokens);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      loadUsers(0, paginationModel.pageSize, initialTokens, newFilterModel);
    }, 400);
  };

  const loadTemplates = async () => {
    await getAppTemplateDomainById(selectedDomain)
      .then(response => {
        setTemplates(response.data)
      })
      .finally(() => { })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadDomainUserAttribute = async (userId) => {
    showLoader();
    await getAppDomainUserAttrByUserId(userId)
      .then(response => {
        setDomainUserAttr(response.data?.[0] || response.data)
      })
      .finally(() => hideLoader())
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadKeywords = () => {
    getAppKeywordDomainById(selectedDomain)
      .then(response => {
        setKeywords(response.data)
      })
      .catch(error => console.error('Error fetching keywords:', error));
  };

  // ============================================
  // ✅ NUEVA FUNCIÓN: Contar usuarios sin fechas
  // ============================================
  const getUsersWithoutDates = () => {
    if (!Array.isArray(users) || users.length === 0) return 0;
    
    return users.filter(user => {
      // Verificar si birthday está vacío o no es un Timestamp válido
      const hasNoBirthday = !user.birthday || 
                           user.birthday === '' || 
                           user.birthday === null ||
                           (typeof user.birthday === 'object' && !user.birthday._seconds && !user.birthday.seconds);
      
      // Verificar si aniversary está vacío o no es un Timestamp válido
      const hasNoAniversary = !user.aniversary || 
                             user.aniversary === '' || 
                             user.aniversary === null ||
                             (typeof user.aniversary === 'object' && !user.aniversary._seconds && !user.aniversary.seconds);
      
      // Retornar true si AMBOS campos están vacíos
      return hasNoBirthday && hasNoAniversary;
    }).length;
  };

  // ============================================
  // ✅ NUEVA FUNCIÓN: Contar usuarios CON fechas
  // ============================================
  const getUsersWithDates = () => {
    if (!Array.isArray(users) || users.length === 0) return 0;
    
    return users.filter(user => {
      // Verificar si birthday es un Timestamp válido
      const hasBirthday = user.birthday && 
                         user.birthday !== '' && 
                         user.birthday !== null &&
                         (typeof user.birthday === 'object' && (user.birthday._seconds || user.birthday.seconds));
      
      // Verificar si aniversary es un Timestamp válido
      const hasAniversary = user.aniversary && 
                           user.aniversary !== '' && 
                           user.aniversary !== null &&
                           (typeof user.aniversary === 'object' && (user.aniversary._seconds || user.aniversary.seconds));
      
      // Retornar true si tiene AL MENOS una de las dos fechas
      return hasBirthday || hasAniversary;
    }).length;
  };

  const handleOpenDeleteModal = async (user) => {
    if (user?.id) {
      await loadDomainUserAttribute(user.id)
    }
    setSelectedUser(user);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedUser(null);
  };

  const handleOpenModal = (user) => {
    if (user?.id) {
      loadDomainUserAttribute(user.id)
    }
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
  };

  const transformToObject = (list) => {
    return list.reduce((acc, item) => {
      acc[item.name] = item.value;
      return acc;
    }, {});
  };

  const handleSaveUser = async (data, dynamicInputs, isNew) => {
    try {
      showLoader();
      let userMessage = isNew ? 'El usuario se creó correctamente' : 'El usuario se guardó correctamente';
      if (isNew) {
        const response = await createAppUser({ ...data, domain_id: selectedDomain })
        await createAppDomainUserAttr({ ...transformToObject(dynamicInputs), domain_user_id: response.id })
      } else {
        await updateAppUser(selectedUser.id, data)
        await updateAppDomainUserAttr(domainUserAttr.id, { ...transformToObject(dynamicInputs) })
      }

      setOpenModal(false);
      setSelectedUser(null);
      setDomainUserAttr(null);
      setOpenSnackbar({ open: true, text: userMessage, error: false });
      loadUsers();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      showLoader();

      const response = await deleteAppUserAll(id);
      if (response?.status !== 204) {
        throw new Error('Eliminación fallida');
      }

      await deleteAppDomainUserAttrAll(domainUserAttr.id);
      setOpenDeleteModal(false);
      setSelectedUser(null);
      setDomainUserAttr(null);
      setOpenSnackbar({ open: true, text: 'El usuario se eliminó correctamente', error: false });
      loadUsers();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar el usuario', error: true });
    } finally {
      hideLoader();
    }
  };

  /**
   * Función para actualizar múltiples usuarios con un nuevo template (Edición Masiva)
   */
  const handleMassUpdateTemplate = async (selectedUsers, templateId) => {
    try {
      showLoader();

      // Actualizar cada usuario individualmente
      const updatePromises = selectedUsers.map(user => 
        updateAppUser(user.id, { 
          ...user,
          signature_template_id: templateId 
        })
      );

      await Promise.all(updatePromises);

      setOpenSnackbar({ 
        open: true, 
        text: `Template actualizado para ${selectedUsers.length} usuario(s)`, 
        error: false 
      });

      // Limpiar selección
      setSelectedUsersForMassEdit([]);
      setOpenMassEditModal(false);

      // Recargar la lista de usuarios
      loadUsers();
    } catch (error) {
      console.error('Error en actualización masiva:', error);
      setOpenSnackbar({ 
        open: true, 
        text: 'Hubo un error al actualizar los templates', 
        error: true 
      });
      throw error;
    } finally {
      hideLoader();
    }
  };

  const handleOpenSyncModal = (user) => {
    setUserToSync(user);
    setOpenSyncModal(true);
  };

  const handleCloseSyncModal = () => {
    setOpenSyncModal(false);
    setUserToSync(null);
  };

  const handleConfirmSyncUser = async () => {
    if (!userToSync?.id) return;
    try {
      showLoader();
      const res = await updateAppUserEmailSignature(userToSync.id);
      const successMsg = res?.data?.message || `Firma en Gmail actualizada exitosamente para ${userToSync.email}`;
      setOpenSnackbar({
        open: true,
        text: successMsg,
        error: false
      });
      loadUsers();
    } catch (error) {
      console.error('Error sincronizando firma de usuario:', error);
      const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Error al actualizar la firma en Gmail';
      setOpenSnackbar({
        open: true,
        text: typeof errMsg === 'string' ? errMsg : 'Error al actualizar la firma en Gmail',
        error: true
      });
      loadUsers();
    } finally {
      hideLoader();
      setOpenSyncModal(false);
      setUserToSync(null);
    }
  };

  /**
   * Función para recibir los usuarios seleccionados desde el DataGrid
   */
  const handleSelectionChange = (selectedIds) => {
    const selectedUsers = users.filter(user => selectedIds.includes(user.id));
    setSelectedUsersForMassEdit(selectedUsers);
  };

  /**
   * Función para abrir el modal de edición masiva
   */
  const handleOpenMassEditModal = () => {
    setOpenMassEditModal(true);
  };

  /**
   * Función para cerrar el modal de edición masiva
   */
  const handleCloseMassEditModal = () => {
    setOpenMassEditModal(false);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  useEffect(() => {
    hideLoader();
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [hideLoader]);

  useEffect(() => {
    if (selectedDomain) {
      loadTemplates();
      loadKeywords();
      const initialTokens = { 0: null };
      const initialFilter = { items: [] };
      setPageTokens(initialTokens);
      setFilterModel(initialFilter);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      loadUsers(0, paginationModel.pageSize, initialTokens, initialFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain])

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon'>
                <GroupIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Total
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {rowCount > 0 ? rowCount : (Array.isArray(users) ? users.length : 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon active-users'>
                <GroupIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Usuarios activos
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {Array.isArray(users) && users?.length ? users.filter(u => u.is_active).length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* ============================================ */}
        {/* ✅ NUEVO CARD: Usuarios con fechas */}
        {/* ============================================ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="success" aria-label="with-dates" className='resume-icon'>
                <EventAvailableIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Con fechas
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {getUsersWithDates()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* ============================================ */}
        {/* ✅ NUEVO CARD: Usuarios sin fechas */}
        {/* ============================================ */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="warning" aria-label="without-dates" className='resume-icon'>
                <EventBusyIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Sin fechas
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {getUsersWithoutDates()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {user?.isAdmin ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2 
        }}>
          <Button 
            variant="contained" 
            startIcon={<PersonAddIcon />} 
            onClick={() => handleOpenModal({})}
          >
            Crear usuario
          </Button>
          
          {/* Botón de Edición Masiva - Solo visible cuando hay selección */}
          {selectedUsersForMassEdit.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedUsersForMassEdit.length} seleccionado(s)
              </Typography>
              <Button 
                variant="contained" 
               // color="secondary"
                color="primary"
                startIcon={<EditNoteIcon />}
                onClick={handleOpenMassEditModal}
              >
                Edición Masiva
              </Button>
            </Box>
          )}
        </Box>
      ) : null}

      <UsersDataGrid
        users={users}
        loading={loadingUsers}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        handleOpenDeleteModal={handleOpenDeleteModal}
        handleOpenModal={handleOpenModal}
        handleOpenSyncModal={handleOpenSyncModal}
        templates={templates}
        onMassUpdate={handleMassUpdateTemplate}
        onSelectionChange={handleSelectionChange}
      />


      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedUser}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteUser}
        message={'Esta seguro de eliminar usuario: ' + selectedUser?.email}
      />

      <UserModal
        keywords={keywords}
        templates={templates}
        domainUserAttr={domainUserAttr}
        openModal={openModal}
        selectedUser={selectedUser}
        handleCloseModal={handleCloseModal}
        handleSaveUser={handleSaveUser}
      />

      <MassEditTemplateModal
        open={openMassEditModal}
        onClose={handleCloseMassEditModal}
        selectedUsers={selectedUsersForMassEdit}
        templates={templates}
        onSave={handleMassUpdateTemplate}
      />

      <Dialog
        open={openSyncModal}
        onClose={handleCloseSyncModal}
        aria-labelledby="sync-user-dialog-title"
      >
        <DialogTitle id="sync-user-dialog-title">
          Actualizar Firma en Gmail
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas actualizar la firma en Gmail para <strong>{userToSync?.email}</strong> ahora mismo?
            <br /><br />
            Esto generará la firma con la plantilla y atributos actuales y la aplicará de inmediato en la cuenta de Gmail del usuario.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleConfirmSyncUser} color="primary" variant="contained" autoFocus>
            Actualizar Firma
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UsersList;